import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArgovLogo } from "./ArgovLogo";
import { ARGOVAcronym } from "./ARGOVAcronym";
import { NAV } from "@/constants/testIds";

const links = [
  { path: "/", label: "Platform", testId: NAV.platform, matches: ["/"] },
  { path: "/#solutions", label: "Solutions", testId: NAV.solutions, matches: [] },
  { path: "/frameworks", label: "Frameworks", testId: NAV.frameworks, matches: ["/frameworks"] },
  { path: "/compliance", label: "Compliance", testId: NAV.compliance, matches: ["/compliance"] },
  { path: "/team", label: "Team", testId: NAV.team, matches: ["/team"] },
  { path: "/enterprise", label: "Enterprise", testId: NAV.enterprise, matches: ["/enterprise"] },
  { path: "/trust", label: "Trust", testId: "nav-link-trust", matches: ["/trust"] },
  { path: "/contact", label: "Contact", testId: NAV.contact, matches: ["/contact"] },
];

export const Navigation = () => {
  const loc = useLocation();
  const pathname = loc.pathname;
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Announcement bar */}
      <div className="argov-announce" style={{ background: "#080f1e", padding: "10px 24px", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
        <span className="argov-pulse" style={{ width: 7, height: 7, borderRadius: "50%", background: "#0891b2", display: "inline-block", flexShrink: 0 }} />
        <span className="font-mono" style={{ fontSize: 11, letterSpacing: "0.12em", color: "#8fa3c8", textTransform: "uppercase" }}>
          AI GOVERNANCE IS ENFORCED AT RUNTIME — NOT IN A POLICY DOCUMENT
        </span>
      </div>

      {/* Nav */}
      <nav className="argov-primary-nav" style={{ background: "#ffffff", borderBottom: "1px solid #e2e8f0", padding: "0 48px", display: "flex", alignItems: "center", height: 68, position: "sticky", top: 0, zIndex: 100, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <Link to="/" data-testid={NAV.logo} style={{ display: "flex", alignItems: "center", gap: 12, marginRight: 40, flexShrink: 0 }}>
          <ArgovLogo />
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
            <span style={{ fontSize: 17, fontWeight: 800, color: "#0c1a2e", letterSpacing: "-0.02em" }}>
              ARGOV<span style={{ color: "#0891b2", fontWeight: 500 }}>™</span>
            </span>
            <span className="font-mono" style={{ fontSize: 9, letterSpacing: "0.06em", color: "#94a3b8", textTransform: "uppercase", marginTop: 4, whiteSpace: "nowrap" }}>
              <ARGOVAcronym color="#0891b2" base="#94a3b8" weight={700} />
            </span>
          </div>
        </Link>

        <div className="argov-nav-links" style={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
          {links.map((l) => {
            const active = l.matches.includes(pathname);
            const style = {
              padding: "8px 14px",
              fontSize: 14,
              color: active ? "#0c1a2e" : "#4a5568",
              fontWeight: active ? 600 : 400,
              borderBottom: active ? "2px solid #0891b2" : "2px solid transparent",
              marginBottom: -1,
            };
            if (l.path.startsWith("/#")) {
              return (
                <a key={l.label} data-testid={l.testId} href={l.path} style={style} className="argov-nav-link">
                  {l.label}
                </a>
              );
            }
            return (
              <Link key={l.label} data-testid={l.testId} to={l.path} style={style} className="argov-nav-link">
                {l.label}
              </Link>
            );
          })}
        </div>

        <div className="argov-nav-actions" style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <Link to="/login" data-testid={NAV.login} style={{ padding: "9px 20px", fontSize: 14, fontWeight: 500, border: "1.5px solid #cbd5e1", borderRadius: 6, color: "#0c1a2e" }}>
            Client Login
          </Link>
          <Link to="/contact" data-testid={NAV.demo} style={{ padding: "9px 22px", fontSize: 14, fontWeight: 600, background: "#0891b2", color: "#ffffff", borderRadius: 6 }}>
            Request a Demo
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          data-testid="nav-mobile-toggle"
          className="argov-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle navigation"
          style={{
            marginLeft: "auto", width: 42, height: 42, borderRadius: 8,
            border: "1.5px solid #cbd5e1", background: "#fff",
            alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, padding: 0,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            {open ? (
              <path d="M6 6l12 12M18 6L6 18" stroke="#0c1a2e" strokeWidth="2" strokeLinecap="round" />
            ) : (
              <g stroke="#0c1a2e" strokeWidth="2" strokeLinecap="round">
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </g>
            )}
          </svg>
        </button>
      </nav>

      {/* Mobile drawer */}
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(12,26,46,0.5)", zIndex: 98 }} />
          <div data-testid="nav-mobile-drawer" style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "84vw", maxWidth: 340, background: "#fff", zIndex: 99, boxShadow: "-12px 0 32px rgba(0,0,0,0.2)", padding: "22px 20px", display: "flex", flexDirection: "column", gap: 4, overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: "#0c1a2e", letterSpacing: "-0.02em" }}>ARGOV<span style={{ color: "#0891b2", fontWeight: 500 }}>™</span></span>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6, color: "#0c1a2e", fontSize: 22, lineHeight: 1 }}>×</button>
            </div>
            {links.map((l) => (
              l.path.startsWith("/#") ? (
                <a key={l.label} href={l.path} onClick={() => setOpen(false)} style={mobileLinkStyle(l.matches.includes(pathname))}>{l.label}</a>
              ) : (
                <Link key={l.label} to={l.path} onClick={() => setOpen(false)} style={mobileLinkStyle(l.matches.includes(pathname))}>{l.label}</Link>
              )
            ))}
            <div style={{ marginTop: 18, borderTop: "1px solid #e2eaf2", paddingTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
              <Link to="/login" onClick={() => setOpen(false)} style={{ padding: "11px 16px", fontSize: 14, fontWeight: 500, border: "1.5px solid #cbd5e1", borderRadius: 8, color: "#0c1a2e", textAlign: "center" }}>Client Login</Link>
              <Link to="/contact" onClick={() => setOpen(false)} style={{ padding: "11px 16px", fontSize: 14, fontWeight: 600, background: "#0891b2", color: "#fff", borderRadius: 8, textAlign: "center" }}>Request a Demo</Link>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const mobileLinkStyle = (active) => ({
  padding: "12px 14px",
  fontSize: 15,
  color: active ? "#0891b2" : "#0c1a2e",
  fontWeight: active ? 700 : 500,
  borderRadius: 8,
  background: active ? "#f0f9ff" : "transparent",
  display: "block",
});
